import { Suspense, lazy, type RefObject } from 'react';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { logger } from '@/lib/logger';
import { TopBar } from '@/components/shell/TopBar';
import { ViewOnlyTopBar } from '@/components/workflow/ViewOnlyTopBar';
import { ScreenReaderAnnouncer } from '@/components/workflow/ScreenReaderAnnouncer';
import { ExplainBlockPanel } from '@/components/workflow/ExplainBlockPanel';
import { AiCostPreviewCard } from '@/components/ai/AiCostPreviewCard';
import { RegressionWarningDialog } from '@/components/editor/dialogs/RegressionWarningDialog';
import { SheetTabBar } from '@/components/workflow/SheetTabBar';
import { WorkflowStatusBar } from '@/components/workflow/WorkflowStatusBar';
import { WorkflowWizardDialog } from '@/components/editor/dialogs/WorkflowWizardDialog';
import { NewWorkflowDialog } from '@/components/editor/dialogs/NewWorkflowDialog';
import { CanvasArea } from '@/gui/layout/CanvasArea';
import { useWorkflow } from '@/contexts/WorkflowContext';
import { useSelectionMode } from '@/contexts/SelectionModeContext';
import { useDialogs } from '@/contexts/DialogContext';
import type { BlockType, PortSide } from '@/types/workflow';
import type { ActionEntry } from '@/hooks/useActionLog';
import type { MinimapData } from '@/components/editor/WorkflowCanvas';
import type { RegressionResult } from '@/lib/artifactRegressionCheck';
import type { ConnectionDebugInfo } from '@/hooks/useDocuments';

const ThreeDView = lazy(() => import('@/components/workflow/ThreeDView').then(m => ({ default: m.ThreeDView })));

/**
 * Desktop-branch JSX extraherad ur Index.tsx.
 *
 * Innehavet är oblandat — TopBar/ViewOnlyTopBar, AI overlays
 * (cost-preview + regression), explain-panel, sheet tabs, 3D view,
 * coop-loading-overlay, canvas + status-bar, wizard- och new-flow-
 * dialoger. Allt flyttat 1:1 från Index.tsx. Många props eftersom
 * desktop-flyget måste se hela apptillståndet — men Index.tsx blir
 * väsentligt mindre och får därmed plats i en single-tool-call upload.
 */
export interface IndexDesktopShellProps {
  // Mode
  viewOnly: boolean;

  // Cross-sheet linking — owned by useCrossSheetLinking, not in selection-mode
  // context (it has its own source-block ref + Esc handler).
  crossSheetMode: boolean;
  crossSheetSource: { sheetIndex: number; blockId: string } | null;

  // Document / connection
  currentDocumentId: string | null;
  isConnected: boolean;
  connectionDebugInfo: ConnectionDebugInfo;
  isCoopLoading: boolean;
  isDirty: boolean;
  realtime: { isSyncing: boolean; collaborators: string[] };
  limits: { maxSheets: number };

  // AI overlays
  aiPreview: { proposalName: string; costCredits: number; balanceBefore: number } | null;
  aiPreviewPending: boolean;
  regressionDialog: { open: boolean; result: RegressionResult | null };
  explainBlockId: string | null;

  // 3D mode
  viewMode: '2d' | '3d';
  setViewMode: (mode: '2d' | '3d') => void;

  // Action log
  actionLogEntries: ActionEntry[];

  // Refs
  canvasInnerRef: RefObject<HTMLDivElement>;

  /** Mobile gesture-handlers (touch-screen-laptops får dem på desktop med). */
  mobileGestures?: import('@/components/editor/WorkflowCanvas').MobileGestureHandlers;

  // Tool palette
  toolTypes: Array<{ type: BlockType; label: string; icon?: string }>;

  // Minimap data prop forwarded to CanvasArea
  setMinimapData: (d: MinimapData) => void;

  // Announcement
  announcement: string | null;

  // Auth
  isAuthenticated: boolean;

  // Top-bar action handlers (wrapped i lockedWhilePreview i caller)
  onSave: () => void;
  onSaveAs: () => void;
  onSaveToCloud: () => void;
  onNewFlow: () => void;
  onLoad: () => void;
  onExportPNG: () => void;
  onSimulate: () => void;
  onClear: () => void;
  onImportJson: () => void;
  onCopyJson: () => void;
  onHelp: () => void;
  onAiJson: () => void;
  onAiAssistant: () => void;
  onPreview: () => void;
  onLogin: () => void;
  onCloudWorkspace: () => void;
  onShareWorkspace: () => void;
  onRepoUpload?: () => void;

  onProcessMotor?: () => void;
  onReopenWizard: () => void;
  onFeatureRequest: () => void;
  onBugReport: () => void;
  onDonate: () => void;
  onUndo: () => void;
  onRedo: () => void;
  canUndo: boolean;
  canRedo: boolean;

  // AI proposal handlers
  onApplyAiProposal: () => void;
  onDiscardAiProposal: () => void;
  onRegressionApplyAnyway: () => void;
  onRegressionRetry: () => void;
  onRegressionCancel: () => void;
  onRegressionOpenChange: (open: boolean) => void;

  // Explain panel
  onExplainOpenChange: (open: boolean) => void;

  // Wizard
  onWizardComplete: (...args: unknown[]) => void;

  // New-flow dialog — open flag from DialogContext, just need confirm handler
  onNewWorkflowConfirm: (kind: 'standard' | 'coop' | 'process') => void;
  canRunProcesses: boolean;
  canUseCoop: boolean;

  // Canvas handlers (wrapped — raw mutations come from context internally)
  onClearActionLog: () => void;
  onSelectBlock: (id: string) => void;
  onSelectGroup: (id: string | null) => void;
  onCreateBlock: (type: string, x: number, y: number, nameOverride?: string) => void;
  onUpdateBlockIcon: (id: string, iconKey: string) => void;
  onConnect: (fromId: string, toId: string, fromPort?: PortSide, toPort?: PortSide) => void;
  onToggleGroupSelection: (id: string) => void;
  onCanvasClick: () => void;
  onUpdateGroupPosition: (id: string, x: number, y: number) => void;
  onConvertToLayer: (id: string) => void;
  onFocusBlockProperty: (blockId: string, field: 'name' | 'description' | 'color') => void;
  onCreateGroupFromSelection: (blockIds: string[]) => void;
  onAddBlockToGroup: (groupId: string, blockId: string) => void;
  onDuplicateBlock: (blockId: string) => void;
  onExplainBlock: (blockId: string) => void;
  onDeleteBlock: (blockId: string) => void;
  onToggleGroupCollapse: (groupId: string) => void;
  onToggleGroupLock: (groupId: string) => void;

  // Sidebars (pre-rendered JSX from caller — sparar oss att tråckla
  // alla ToolSidebar/PropertiesPanel-props genom det här interfacet).
  sidebarLeft: React.ReactNode;
  sidebarRight: React.ReactNode;

  // Status-bar zoom — read from minimap data in caller.
  statusBarZoom?: number;
}

export function IndexDesktopShell(props: IndexDesktopShellProps) {
  const {
    viewOnly, aiPreview, aiPreviewPending, regressionDialog, explainBlockId,
    crossSheetMode, crossSheetSource, currentDocumentId, isConnected, connectionDebugInfo,
    isCoopLoading, isDirty, realtime, limits, viewMode, setViewMode,
    actionLogEntries, canvasInnerRef, toolTypes, setMinimapData, announcement,
    isAuthenticated,
    onNewWorkflowConfirm, canRunProcesses, canUseCoop,
    onSave, onSaveAs, onSaveToCloud, onNewFlow, onLoad, onExportPNG, onSimulate,
    onClear, onImportJson, onCopyJson, onHelp, onAiJson, onAiAssistant, onPreview,
    onLogin, onCloudWorkspace, onShareWorkspace, onRepoUpload, onProcessMotor, onReopenWizard,
    onFeatureRequest, onBugReport, onDonate, onUndo, onRedo, canUndo, canRedo,
    onApplyAiProposal, onDiscardAiProposal,
    onRegressionApplyAnyway, onRegressionRetry, onRegressionCancel, onRegressionOpenChange,
    onExplainOpenChange, onWizardComplete,
    onClearActionLog, onSelectBlock, onSelectGroup,
    onCreateBlock, onUpdateBlockIcon, onConnect,
    onToggleGroupSelection, onCanvasClick, onUpdateGroupPosition, onConvertToLayer,
    onFocusBlockProperty, onCreateGroupFromSelection, onAddBlockToGroup,
    onDuplicateBlock, onExplainBlock, onDeleteBlock, onToggleGroupCollapse,
    onToggleGroupLock,
    sidebarLeft, sidebarRight,
    statusBarZoom,
    mobileGestures,
  } = props;

  // Workflow data + sheet + raw mutations from context — no longer drilled.
  const {
    blocks, connectors, groups, selectedBlock, selectedBlockId,
    workflowName, visibleBlocks, visibleConnectors,
    collapsedGroups, expandedGroups, breadcrumbs, layerPath, currentLayerGroupId,
    sheets, activeSheetIndex, crossSheetConnectors,
    setActiveSheetIndex, addSheet, removeSheet, renameSheet,
    duplicateSheet, reorderSheets, updateSheetMeta,
    updateBlockPosition, updateConnectorWaypoints, updateConnectorLabel,
    deleteConnector, deleteGroup, navigateInto, navigateToLevel,
  } = useWorkflow();

  // UI-mode flags from selection-mode context.
  const { selectedGroupId, connectMode, groupMode, groupSelection } = useSelectionMode();
  // Dialog open/close flags from dialog context.
  const dlg = useDialogs();
  const isWizardOpen = dlg.wizardOpen;

  // Derived: explain-block lookup. Previously passed as a prop, now resolved
  // from id + context blocks (single source of truth).
  const explainBlock = explainBlockId
    ? blocks.find(b => b.id === explainBlockId) ?? null
    : null;

  return (
    <>
      <a
        href="#canvas"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:p-3 focus:bg-primary focus:text-primary-foreground focus:rounded-md focus:m-2 focus:outline-none"
      >
        Skip to canvas
      </a>
      <ScreenReaderAnnouncer message={announcement} />
      {/* A-028: wrap AI-preview-overlays i ErrorBoundary level=section. */}
      <ErrorBoundary
        level="section"
        onError={(err) => logger.error('[AiPreviewOverlays] Uncaught error:', err)}
      >
        {aiPreview && (
          <div className="fixed top-16 right-4 z-50 max-w-sm shadow-lg">
            <AiCostPreviewCard
              proposalName={aiPreview.proposalName}
              costCredits={aiPreview.costCredits}
              balanceBefore={aiPreview.balanceBefore}
              onApply={onApplyAiProposal}
              onDiscard={onDiscardAiProposal}
              pending={aiPreviewPending}
            />
          </div>
        )}
        <RegressionWarningDialog
          open={regressionDialog.open}
          onOpenChange={onRegressionOpenChange}
          result={regressionDialog.result}
          onApplyAnyway={onRegressionApplyAnyway}
          onRetry={onRegressionRetry}
          onCancel={onRegressionCancel}
        />
      </ErrorBoundary>
      <ExplainBlockPanel
        open={!!explainBlockId}
        onOpenChange={onExplainOpenChange}
        block={explainBlock}
        connectors={connectors}
        sheetBlocks={blocks}
        workflowName={workflowName}
      />
      {viewOnly ? (
        <ViewOnlyTopBar
          onLoad={onLoad}
          onExportPNG={onExportPNG}
          onPreview={onPreview}
          isConnected={isConnected}
          connectionDebugInfo={connectionDebugInfo}
        />
      ) : (
        <TopBar
          onSave={onSave}
          onSaveAs={onSaveAs}
          onSaveToCloud={onSaveToCloud}
          hasCloudDocument={currentDocumentId !== null}
          onNewFlow={onNewFlow}
          onLoad={onLoad}
          onRepoUpload={onRepoUpload}

          onExportPNG={onExportPNG}
          onSimulate={onSimulate}
          onClear={onClear}
          onImportJson={onImportJson}
          onCopyJson={onCopyJson}
          onHelp={onHelp}
          onAiJson={onAiJson}
          onAiAssistant={onAiAssistant}
          onPreview={onPreview}
          onLogin={onLogin}
          onCloudWorkspace={onCloudWorkspace}
          onShareWorkspace={onShareWorkspace}
          onProcessMotor={onProcessMotor}
          onReopenWizard={onReopenWizard}
          onFeatureRequest={onFeatureRequest}
          onBugReport={onBugReport}
          onDonate={onDonate}
          onUndo={onUndo}
          onRedo={onRedo}
          canUndo={canUndo}
          canRedo={canRedo}
          isConnected={isConnected}
          connectionDebugInfo={connectionDebugInfo}
          currentDocumentTitle={currentDocumentId ? workflowName : undefined}
          hasLoadedDocument={!!currentDocumentId}
          isDirty={isDirty}
          coopActive={realtime.isSyncing}
          coopCollaborators={realtime.collaborators}
        />
      )}

      {!viewOnly && (sheets.length >= 2 || (isAuthenticated && (limits.maxSheets > 1 || limits.maxSheets < 0))) && (
        <SheetTabBar
          sheets={sheets}
          activeSheetIndex={activeSheetIndex}
          crossSheetConnectors={crossSheetConnectors}
          maxSheets={limits.maxSheets}
          onSetActiveSheet={setActiveSheetIndex}
          onAddSheet={addSheet}
          onRemoveSheet={removeSheet}
          onRenameSheet={renameSheet}
          onDuplicateSheet={duplicateSheet}
          onReorderSheets={reorderSheets}
          onUpdateSheetMeta={updateSheetMeta}
        />
      )}

      {viewMode === '3d' && sheets.length >= 2 && (
        <ErrorBoundary
          level="section"
          onError={(err) => logger.error('[ThreeDView] Uncaught error:', err)}
        >
          <Suspense fallback={<div className="fixed inset-0 z-50 bg-background flex items-center justify-center text-muted-foreground">Loading 3D view...</div>}>
            <ThreeDView
              sheets={sheets}
              crossSheetConnectors={crossSheetConnectors}
              activeSheetIndex={activeSheetIndex}
              onClose={() => setViewMode('2d')}
              onNavigateToSheet={(index) => { setActiveSheetIndex(index); setViewMode('2d'); }}
              onSwitchMode={setViewMode}
              overlayScope={currentDocumentId ?? undefined}
            />
          </Suspense>
        </ErrorBoundary>
      )}

      {/* Q-101: co-op join skeleton-loader. */}
      {isCoopLoading && (
        <div
          className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-2 bg-background/80 backdrop-blur-sm pointer-events-none"
          role="status"
          aria-live="polite"
          data-testid="coop-loading-overlay"
        >
          <div className="w-8 h-8 rounded-full border-2 border-primary border-t-transparent animate-spin" aria-hidden="true" />
          <div className="text-sm text-muted-foreground">Laddar delad workflow…</div>
        </div>
      )}

      {isWizardOpen ? (
        <div className="min-h-0 flex-1 bg-background" aria-hidden="true" data-testid="wizard-canvas-suspended" />
      ) : (
        <CanvasArea
          viewOnly={viewOnly}
          sidebarLeft={sidebarLeft}
          sidebarRight={sidebarRight}
          actionLogEntries={actionLogEntries}
          onClearActionLog={onClearActionLog}
          enableFloatingHud

          blocks={blocks} connectors={connectors} groups={groups}
          selectedBlockId={selectedBlockId} selectedGroupId={selectedGroupId}
          connectMode={connectMode} groupMode={groupMode} groupSelection={groupSelection}
          visibleBlocks={visibleBlocks} visibleConnectors={visibleConnectors}
          collapsedGroups={collapsedGroups} expandedGroups={expandedGroups}
          breadcrumbs={breadcrumbs} layerPath={layerPath}
          onNavigateInto={navigateInto} onNavigateToLevel={navigateToLevel}
          onSelectBlock={onSelectBlock} onSelectGroup={onSelectGroup}
          onCreateBlock={onCreateBlock}
          onUpdateBlockPosition={updateBlockPosition}
          onUpdateBlockIcon={onUpdateBlockIcon}
          onConnect={onConnect}
          onToggleGroupSelection={onToggleGroupSelection}
          onCanvasClick={onCanvasClick}
          onMinimapDataChange={setMinimapData}
          onUpdateGroupPosition={onUpdateGroupPosition}
          onConvertToLayer={onConvertToLayer}
          onUpdateConnectorWaypoints={updateConnectorWaypoints}
          onUpdateConnectorLabel={updateConnectorLabel}
          onDeleteConnector={deleteConnector}
          onFocusBlockProperty={onFocusBlockProperty}
          onCreateGroupFromSelection={onCreateGroupFromSelection}
          onAddBlockToGroup={onAddBlockToGroup}
          onDuplicateBlock={onDuplicateBlock}
          onExplainBlock={onExplainBlock}
          onDeleteBlock={onDeleteBlock}
          onToggleGroupCollapse={onToggleGroupCollapse}
          onDeleteGroup={deleteGroup}
          onToggleGroupLock={onToggleGroupLock}
          toolTypes={toolTypes}
          crossSheetMode={crossSheetMode}
          crossSheetSourceBlockId={crossSheetSource?.sheetIndex === activeSheetIndex ? crossSheetSource.blockId : null}
          canvasInnerRef={canvasInnerRef}
          mobileGestures={mobileGestures}
        />
      )}

      {!viewOnly && !isWizardOpen && (
        <WorkflowStatusBar
          blockCount={blocks.length}
          connectorCount={connectors.length}
          groupCount={groups.length}
          selectedBlockName={selectedBlock?.config.name}
          currentLayerName={currentLayerGroupId ? groups.find(g => g.id === currentLayerGroupId)?.name || 'Layer' : undefined}
          zoom={statusBarZoom}
        />
      )}

      <WorkflowWizardDialog open={dlg.wizardOpen} onComplete={onWizardComplete} />
      <NewWorkflowDialog
        open={dlg.newWorkflowOpen}
        onOpenChange={dlg.setNewWorkflowOpen}
        onConfirm={onNewWorkflowConfirm}
        canRunProcesses={canRunProcesses}
        canUseCoop={canUseCoop}
      />
    </>
  );
}
